import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators'

import { ALL_COMPANIES_OPTION } from 'utils/partnerUtils'
import { toCamelCase } from 'utils/strings'

import {
  DELIVERY,
  PARTICIPANTS_ALL_CATEGORY,
  PARTICIPANTS_EXCLUDE_FILTER_VALUES,
  PARTICIPANTS_NAMES_CATEGORY,
  PARTICIPANTS_PER_PAGE,
} from 'containers/AwarenessCenter/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { addToast } from '../../../Toasts/actions'

import {
  getGroupAutonomousConfigMemberError,
  getGroupAutonomousConfigMemberSuccess,
  updateParticipantsNumbersError,
  updateParticipantsNumbersSuccess,
} from './actions'
import {
  CHANGE_MSP_PARTICIPANTS_MODAL_PAGE,
  CHANGE_PARTICIPANTS_SELECTION,
  CHANGE_PARTICIPANTS_TOTAL_SELECTION,
  CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
  DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
  FETCH_AUTONOMOUS_SCHEDULE_ASYNC,
  FETCH_PARTICIPANTS_DATA_ASYNC,
  GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
  GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC,
  GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC,
  GET_PARTICIPANTS_ASYNC,
  GET_PARTICIPANTS_INFO_ASYNC,
  MEMBERS_PER_PAGE,
  SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC,
  SEARCH_PARTICIPANTS_INFO_ASYNC,
  SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC,
  UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
} from './constants'
import { selectMembers, selectParticipants } from './selectors'

const fetchAutonomousScheduleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE),
    switchMap(() =>
      api.awarenessCenter
        .fetchAutonomousSchedule(state$.value.partnerOptions.selectedCompany?.value)
        .pipe(
          map((response) =>
            FETCH_AUTONOMOUS_SCHEDULE_ASYNC.onSuccess({
              data: response,
            })
          ),
          catchError((error) =>
            of(epicsErrorHandler(FETCH_AUTONOMOUS_SCHEDULE_ASYNC.onError, error, []))
          ),
          takeUntil(action$.pipe(ofType(FETCH_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL)))
        )
    )
  )

const getGroupAutonomousScheduleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE),
    switchMap(() =>
      api.awarenessCenter.getGroupAutonomousSchedule().pipe(
        map((response) => GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.onSuccess({ data: response })),
        catchError((error) =>
          of(epicsErrorHandler(GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.onError, error, []))
        ),
        takeUntil(action$.pipe(ofType(GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL)))
      )
    )
  )

const getGroupAutonomousConfigMemberEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE,
      CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS,
      DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS,
      CHANGE_MSP_PARTICIPANTS_MODAL_PAGE,
      UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (
        action.type === CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE &&
        !state$.value.awarenessCenter.autonomousScheduler.isMemberRemoving
      ) {
        return false
      } else {
        return true
      }
    }),
    switchMap((action) => {
      const successCallback =
        action.type === UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS
          ? updateParticipantsNumbersSuccess
          : getGroupAutonomousConfigMemberSuccess
      const errorCallback =
        action.type === UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SEARCH_PARTICIPANTS_INFO_ASYNC
          ? updateParticipantsNumbersError
          : getGroupAutonomousConfigMemberError

      return api.awarenessCenter
        .getGroupAutonomousConfigMember(selectMembers(state$.value).page, MEMBERS_PER_PAGE)
        .pipe(
          map((response) => successCallback(response)),
          catchError((error) => of(epicsErrorHandler(errorCallback, error, [404]))),
          takeUntil(action$.pipe(ofType(GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL)))
        )
    })
  )

const getGroupAutonomousConfigMemberCandidatesEpic = (
  action$,
  state$,
  { api, epicsErrorHandler }
) =>
  action$.pipe(
    ofType(GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC.INITIATE),
    switchMap(() =>
      api.awarenessCenter.getGroupAutonomousConfigMemberCandidates().pipe(
        map((response) =>
          GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC.onSuccess({ data: response })
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(
              GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC.onError,
              error,
              []
            )
          )
        ),
        takeUntil(action$.pipe(ofType(GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC.CANCEL)))
      )
    )
  )

const createGroupAutonomousConfigMemberEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .createGroupAutonomousConfigMember({
          companyId: action.payload.companyId,
          participantsChoices: {
            [PARTICIPANTS_ALL_CATEGORY]: [PARTICIPANTS_ALL_CATEGORY],
          },
        })
        .pipe(
          map((response) =>
            CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.onSuccess({ data: response })
          ),
          catchError((error) =>
            of(epicsErrorHandler(CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.onError, error, []))
          ),
          takeUntil(action$.pipe(ofType(CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL)))
        )
    )
  )

const deleteGroupAutonomousConfigMemberEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter.deleteGroupAutonomousConfigMember(action.payload.companyId).pipe(
        map((response) =>
          DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.onSuccess({ data: response })
        ),
        catchError((error) =>
          of(epicsErrorHandler(DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.onError, error, []))
        ),
        takeUntil(action$.pipe(ofType(DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL)))
      )
    )
  )

const updateGroupAutonomousConfigMemberEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .updateGroupAutonomousConfigMember(action.payload.companyId, action.payload.data)
        .pipe(
          map((response) =>
            UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.onSuccess({ data: response })
          ),
          catchError((error) =>
            of(epicsErrorHandler(UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.onError, error, []))
          ),
          takeUntil(action$.pipe(ofType(UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL)))
        )
    )
  )

const saveGroupAutonomousScheduleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .saveGroupAutonomousSchedule({
          months: action.payload.months,
          forceSave: action.payload.forceSave,
          membersSelectionData: {
            allItems: action.payload.allItems,
            excludeIds: action.payload.excludeIds,
          },
        })
        .pipe(
          mergeMap(() =>
            of(
              SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.onSuccess(),
              addToast({
                message: action.payload.months.length
                  ? 'Autonomous campaign successfully scheduled!'
                  : 'Changes successfully saved!',
              })
            )
          ),
          catchError((error) =>
            of(epicsErrorHandler(SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL)))
        )
    )
  )

const submitAutonomousScheduleEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE),
    switchMap((action) =>
      api.awarenessCenter
        .submitAutonomousSchedule(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.months,
          selectParticipants(state$.value).included,
          selectParticipants(state$.value).excluded
        )
        .pipe(
          mergeMap((response) =>
            of(
              SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC.onSuccess({
                data: response,
                months: action.payload.months,
              }),
              addToast({
                message: action.payload.months.length
                  ? 'Autonomous campaign successfully scheduled!'
                  : 'Changes successfully saved!',
              })
            )
          ),
          catchError((error) =>
            of(epicsErrorHandler(SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC.onError, error, []))
          ),
          takeUntil(action$.pipe(ofType(SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL)))
        )
    )
  )

const getParticipantsInfoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_AUTONOMOUS_SCHEDULE_ASYNC.SUCCESS,
      GET_PARTICIPANTS_INFO_ASYNC.INITIATE,
      CHANGE_PARTICIPANTS_SELECTION,
      CHANGE_PARTICIPANTS_TOTAL_SELECTION
    ),
    filter((action) => {
      const companyFilter = selectCompanyFilter(state$.value)
      const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label

      if (
        isAllCompanySelected &&
        [CHANGE_PARTICIPANTS_SELECTION, CHANGE_PARTICIPANTS_TOTAL_SELECTION].includes(action.type)
      ) {
        return false
      } else {
        return true
      }
    }),
    mergeMap((action) => {
      const companyFilter = selectCompanyFilter(state$.value)
      const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label
      const companyId = isAllCompanySelected
        ? action.payload.companyId
        : state$.value.partnerOptions.selectedCompany?.value

      return api.wizardCampaign
        .getParticipantsInfo(companyId, {
          deliveryType: DELIVERY.EMAIL,
          groups: selectParticipants(state$.value).included,
          excludedGroups: selectParticipants(state$.value).excluded,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              GET_PARTICIPANTS_INFO_ASYNC.onSuccess({
                data: response,
                index: action.payload?.index,
              })
            )
          ),
          catchError((error) =>
            of(epicsErrorHandler(GET_PARTICIPANTS_INFO_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(GET_PARTICIPANTS_INFO_ASYNC.CANCEL)))
        )
    })
  )

const searchParticipantsInfoEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.INITIATE),
    mergeMap((action) => {
      const companyFilter = selectCompanyFilter(state$.value)
      const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label
      const companyId = isAllCompanySelected
        ? action.payload.companyId
        : state$.value.partnerOptions.selectedCompany?.value

      return api.wizardCampaign
        .getParticipantsInfo(companyId, {
          searchFilter: action.payload.value,
          deliveryType: DELIVERY.EMAIL,
          groups: selectParticipants(state$.value).included,
          excludedGroups: selectParticipants(state$.value).excluded,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(
              SEARCH_PARTICIPANTS_INFO_ASYNC.onSuccess({
                data: response,
                index: action.payload?.index,
              })
            )
          ),
          catchError((error) =>
            of(epicsErrorHandler(SEARCH_PARTICIPANTS_INFO_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.CANCEL)))
        )
    })
  )

const fetchParticipantsDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_AUTONOMOUS_SCHEDULE_ASYNC.SUCCESS, FETCH_PARTICIPANTS_DATA_ASYNC.INITIATE),
    mergeMap((action) =>
      api.wizardCampaign
        .getSelectedParticipantsDetails(state$.value.partnerOptions.selectedCompany?.value, {
          deliveryType: DELIVERY.EMAIL,
          groups: action.payload.data.participants_choices || action.payload.data.groups,
          excludedGroups:
            action.payload.data.participants_exclude_choices || action.payload.data.excludedGroups,
        })
        .pipe(
          mergeMap(({ response }) =>
            of(FETCH_PARTICIPANTS_DATA_ASYNC.onSuccess({ data: response }))
          ),
          catchError((error) =>
            of(epicsErrorHandler(FETCH_PARTICIPANTS_DATA_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(FETCH_PARTICIPANTS_DATA_ASYNC.CANCEL)))
        )
    )
  )

const getParticipantsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_PARTICIPANTS_ASYNC.INITIATE, GET_PARTICIPANTS_INFO_ASYNC.SUCCESS),
    switchMap((action) => {
      const companyFilter = selectCompanyFilter(state$.value)
      const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label
      const companyId = isAllCompanySelected
        ? action.payload.companyId
        : state$.value.partnerOptions.selectedCompany?.value
      let showIncluded
      let showExcluded
      if (action.type === GET_PARTICIPANTS_INFO_ASYNC.SUCCESS) {
        showIncluded = true
        showExcluded = false
      } else {
        // modal case
        const excludeFilter = selectParticipants(state$.value).excludeFilter
        showIncluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.INCLUDED
        showExcluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.EXCLUDED
      }

      const participants = selectParticipants(state$.value)
      const include = {
        allCompany: false,
        show: showIncluded,
      }
      const exclude = {
        show: showExcluded,
      }

      Object.keys(participants.included).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_ALL_CATEGORY) {
          include.allCompany = true
        } else if (key === PARTICIPANTS_NAMES_CATEGORY) {
          include.emails = participants.included[key]
        } else {
          include[formattedKey] = participants.included[key]
        }
      })

      Object.keys(participants.excluded).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_NAMES_CATEGORY) {
          exclude.emails = participants.excluded[key]
        } else {
          exclude[formattedKey] = participants.excluded[key]
        }
      })

      if (include.emails?.length === 0) delete include.emails
      if (exclude.emails?.length === 0) delete exclude.emails

      return api.wizardCampaign
        .getParticipants(
          companyId,
          {
            search: selectParticipants(state$.value).modalSearch,
            deliveryType: DELIVERY.EMAIL,
            include,
            exclude,
          },
          selectParticipants(state$.value).page,
          PARTICIPANTS_PER_PAGE
        )
        .pipe(
          mergeMap(({ response }) => of(GET_PARTICIPANTS_ASYNC.onSuccess({ data: response }))),
          catchError((error) =>
            of(epicsErrorHandler(GET_PARTICIPANTS_ASYNC.onError, error, [400]))
          ),
          takeUntil(action$.pipe(ofType(GET_PARTICIPANTS_ASYNC.CANCEL)))
        )
    })
  )

export default combineEpics(
  fetchAutonomousScheduleEpic,
  submitAutonomousScheduleEpic,
  getParticipantsInfoEpic,
  getParticipantsEpic,
  searchParticipantsInfoEpic,
  fetchParticipantsDataEpic,
  getGroupAutonomousScheduleEpic,
  getGroupAutonomousConfigMemberEpic,
  getGroupAutonomousConfigMemberCandidatesEpic,
  createGroupAutonomousConfigMemberEpic,
  deleteGroupAutonomousConfigMemberEpic,
  updateGroupAutonomousConfigMemberEpic,
  saveGroupAutonomousScheduleEpic
)
